import { useRequiredContext } from "@redotech/react-util/context";
import { AiFileAnalysis } from "@redotech/redo-common-model/file/uploaded-file";
import { Uploader, UploaderContext } from "@redotech/redo-web/image-upload";
import { ReactNode } from "react";
import { RedoMerchantClient } from ".";
import { RedoMerchantClientContext } from "./context";
import {
  createAttachmentUpload,
  createDocumentUpload,
  createUpload,
} from "./team";

export function clientUploader(client: RedoMerchantClient): Uploader {
  return async ({
    file,
    maxFileSizeKb,
    maxImageWidthPx,
    recommendedLongestSidePx,
    autoOptimizeImage,
    preserveGifs,
    signal,
  }) => {
    // Check if this is a document file (HTML or EML)
    const isDocument =
      file.type === "text/html" ||
      file.name.endsWith(".html") ||
      file.name.endsWith(".eml") ||
      file.type === "message/rfc822";

    // Check if this is an image file
    const isImage = file.type.startsWith("image/");

    let result: { url: string; analysis?: AiFileAnalysis };
    // TODO: should AI analysis should explicitly be opted into rather than inferred by file type?
    if (isDocument) {
      // Use document upload endpoint for HTML/EML files
      result = await createDocumentUpload(client, { file, signal });
    } else if (isImage) {
      // Use regular upload endpoint for images (with Sharp processing)
      result = await createUpload(client, {
        file,
        maxFileSizeKb,
        maxImageWidthPx,
        recommendedLongestSidePx,
        autoOptimizeImage,
        preserveGifs,
        signal,
      });
    } else {
      // Use attachment upload endpoint for non-image files (no Sharp processing)
      result = await createAttachmentUpload(client, {
        file,
        maxFileSizeKb,
        signal,
      });
    }
    return { url: new URL(result.url), analysis: result.analysis };
  };
}

export function ClientUploader({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  return (
    <UploaderContext.Provider value={clientUploader(client)}>
      {children}
    </UploaderContext.Provider>
  );
}
